import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * useRoninCueAdmin — all data + bulk-action logic for the Ronin Box cue admin page
 * (load/filter/stats, per-cue regen, bulk audio generation, numbered-cue fixes,
 * movement autofill, mode/level tagging, TTS suggestions, delete, seed defaults).
 * Extracted from RoninBoxAdmin so the page component is presentational. Behavior unchanged.
 */
export function useRoninCueAdmin() {
  const [cues, setCues] = useState([]);
  const [settings, setSettings] = useState(null);
  const [voices, setVoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [editing, setEditing] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {total, done, succeeded, failed}
  const [showSettings, setShowSettings] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestions, setSuggestions] = useState(null); // {total, suggestions:[{id,text,suggested_tts_text}]}
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState(new Set());
  const [applying, setApplying] = useState(false);

  const openSuggest = async () => {
    setSuggestOpen(true);
    setSuggestLoading(true);
    try {
      const { data } = await api.post("/admin/ronin-box/cues/suggest-tts-overrides", { only_missing: true });
      setSuggestions(data);
      setSelectedSuggestionIds(new Set((data.suggestions || []).map((s) => s.id))); // all selected by default
    } catch { toast.error("Couldn't fetch suggestions"); }
    finally { setSuggestLoading(false); }
  };

  const toggleSuggestion = (id) => {
    setSelectedSuggestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applySuggestions = async () => {
    const picks = (suggestions?.suggestions || []).filter((s) => selectedSuggestionIds.has(s.id));
    if (picks.length === 0) { toast.error("Pick at least one"); return; }
    setApplying(true);
    try {
      // Apply one-by-one via PATCH so each cue's audio-clear hook runs correctly
      for (const s of picks) {
        await api.patch(`/admin/ronin-box/cues/${s.id}`, { tts_text: s.suggested_tts_text });
      }
      toast.success(`Applied ${picks.length} TTS overrides — click ⟳ on each or "Regenerate all" to re-bake audio.`);
      setSuggestOpen(false);
      setSuggestions(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Apply failed");
    } finally { setApplying(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cuesRes, settingsRes] = await Promise.all([
        api.get("/admin/ronin-box/cues"),
        api.get("/admin/ronin-box/settings"),
      ]);
      setCues(cuesRes.data || []);
      setSettings(settingsRes.data);
    } catch (e) {
      toast.error("Failed to load Ronin Box data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadVoices = async () => {
    if (voices) return;
    try {
      const { data } = await api.get("/admin/ronin-box/voices");
      setVoices(data?.voices || []);
    } catch { toast.error("Failed to fetch ElevenLabs voices"); }
  };

  const filtered = useMemo(() => {
    return cues.filter((c) => {
      if (filterCat !== "all" && c.category !== filterCat) return false;
      if (q.trim() && !c.text.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [cues, q, filterCat]);

  const stats = useMemo(() => {
    const total = cues.length;
    const ready = cues.filter((c) => c.audio_status === "ready").length;
    const pending = cues.filter((c) => c.audio_status === "pending").length;
    const failed = cues.filter((c) => c.audio_status === "failed").length;
    const missing = cues.filter((c) => c.audio_status !== "ready" || !c.audio_url).length;
    return { total, ready, pending, failed, missing };
  }, [cues]);

  const regen = async (id) => {
    setCues((cs) => cs.map((c) => (c.id === id ? { ...c, audio_status: "regenerating" } : c)));
    try {
      const { data } = await api.post(`/admin/ronin-box/cues/${id}/regenerate-audio`);
      setCues((cs) => cs.map((c) => (c.id === id ? data : c)));
      if (data.audio_status === "ready") toast.success(`Regenerated: ${data.text}`);
      else toast.error(`Failed: ${data.audio_error || "Unknown error"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  // Shared inner loop for "generate missing" and "regenerate all" — both iterate a target
  // set, regenerate audio one-by-one, and stream progress. Returns {succeeded, failed}.
  const runBulkRegen = async (targets) => {
    setBulkRunning(true);
    setBulkProgress({ total: targets.length, done: 0, succeeded: 0, failed: 0 });
    let done = 0, succeeded = 0, failed = 0;
    for (const t of targets) {
      try {
        const { data } = await api.post(`/admin/ronin-box/cues/${t.id}/regenerate-audio`);
        if (data.audio_status === "ready") succeeded++; else failed++;
        setCues((cs) => cs.map((c) => (c.id === t.id ? data : c)));
      } catch {
        failed++;
      }
      done++;
      setBulkProgress({ total: targets.length, done, succeeded, failed });
    }
    setBulkRunning(false);
    return { succeeded, failed };
  };

  const genMissing = async () => {
    const targets = cues.filter((c) => c.audio_status !== "ready" || !c.audio_url);
    if (targets.length === 0) { toast.info("All cues already have audio."); return; }
    if (!window.confirm(`Generate audio for ${targets.length} cue${targets.length === 1 ? "" : "s"} missing audio? Estimated ${Math.round(targets.length * 1.5)}s. Each phrase makes one ElevenLabs API call.`)) return;
    const { succeeded, failed } = await runBulkRegen(targets);
    toast.success(`Generated ${succeeded}/${targets.length} missing${failed > 0 ? ` (${failed} failed)` : ""}`);
  };

  const regenAll = async () => {
    const targets = cues.filter((c) => c.active);
    if (!window.confirm(`Regenerate audio for ${targets.length} active cues? Estimated ${Math.round(targets.length * 1.5)}s. Each phrase makes one ElevenLabs API call.`)) return;
    const { succeeded, failed } = await runBulkRegen(targets);
    toast.success(`Regenerated ${succeeded}/${targets.length} (${failed} failed)`);
  };

  // One-click fix for the "silent numbered cues" bug — server spells digits as words
  // ("1-2" → "one, two") before sending to ElevenLabs, then re-bakes audio for every
  // cue whose text contains a digit. Two-step: first pass respects custom tts_text
  // overrides; if any cues were skipped, prompts the admin to also overwrite the
  // overrides (since the old tts_text values were usually a failed attempt to fix
  // the silent-audio bug manually).
  const regenNumbered = async () => {
    const targets = cues.filter((c) => /\d/.test(c.text || ""));
    if (targets.length === 0) {
      toast.info("No cues with digits in their text. Nothing to regenerate.");
      return;
    }
    if (!window.confirm(`Regenerate audio for ${targets.length} numbered cue${targets.length === 1 ? "" : "s"} (the ones with digits like "1", "1-2")? Estimated ${Math.round(targets.length * 1.5)}s. The server will spell each digit as a word so ElevenLabs reads them aloud properly.`)) return;
    setBulkRunning(true);
    setBulkProgress({ total: targets.length, done: 0, succeeded: 0, failed: 0 });
    try {
      const { data } = await api.post("/admin/ronin-box/regenerate-numbered", { include_overrides: false });
      setBulkProgress({ total: data.total, done: data.total, succeeded: data.succeeded, failed: data.failed });
      const skippedCount = data.skipped_due_to_tts_override || 0;
      const baseMsg = `Regenerated ${data.succeeded}/${data.total} numbered cues${data.failed > 0 ? ` (${data.failed} failed)` : ""}`;
      if (skippedCount === 0) {
        toast.success(baseMsg);
      } else {
        toast.success(`${baseMsg} — ${skippedCount} skipped (have custom TTS overrides)`);
        // Follow-up: offer to also overwrite the overrides
        const goForce = window.confirm(
          `${skippedCount} numbered cue${skippedCount === 1 ? "" : "s"} ${skippedCount === 1 ? "was" : "were"} skipped because ${skippedCount === 1 ? "it has" : "they have"} a custom TTS override.\n\n` +
          `Often those overrides were a manual attempt to fix the silent-audio bug. The new digit-spelling logic does the job automatically — do you want to OVERWRITE the overrides on those ${skippedCount} cue${skippedCount === 1 ? "" : "s"} so they use the auto-spelled version instead?\n\n` +
          `This will clear their custom tts_text and regenerate audio.`
        );
        if (goForce) {
          setBulkRunning(true);
          setBulkProgress({ total: skippedCount, done: 0, succeeded: 0, failed: 0 });
          const r2 = await api.post("/admin/ronin-box/regenerate-numbered", { include_overrides: true });
          setBulkProgress({ total: r2.data.total, done: r2.data.total, succeeded: r2.data.succeeded, failed: r2.data.failed });
          toast.success(`Re-baked ${r2.data.succeeded}/${r2.data.total} — ${r2.data.overrides_cleared} TTS override${r2.data.overrides_cleared === 1 ? "" : "s"} cleared${r2.data.failed > 0 ? `, ${r2.data.failed} failed` : ""}`);
        }
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Regenerate numbered failed");
    } finally {
      setBulkRunning(false);
    }
  };

  // BULLETPROOF fix — actually rename the cue text from digits to spelled-out words
  // ("1-2-3" → "One-Two-Three"). Clears any tts_text override + regenerates audio.
  // Destructive (changes the on-screen label) so we ask for explicit confirmation.
  const spellOutNumbered = async () => {
    const targets = cues.filter((c) => /\d/.test(c.text || ""));
    if (targets.length === 0) {
      toast.info("No cues with digits in their text. Nothing to rewrite.");
      return;
    }
    const examples = targets.slice(0, 3).map((c) => `"${c.text}"`).join(", ");
    const ok = window.confirm(
      `Rewrite the TEXT of ${targets.length} numbered cue${targets.length === 1 ? "" : "s"} from digits to spelled-out words?\n\n` +
      `Examples: ${examples}${targets.length > 3 ? ", …" : ""} → "One-Two-Three", "Eight-Three-Two", etc.\n\n` +
      `This will:\n` +
      `  • Change each cue's on-screen label (1-2-3 becomes One-Two-Three)\n` +
      `  • Clear any custom TTS overrides on these cues\n` +
      `  • Regenerate audio for every renamed cue (~${Math.round(targets.length * 1.5)}s)\n\n` +
      `Modes, levels, weights, and punch counts are NOT touched. Safe to re-run (idempotent).`
    );
    if (!ok) return;
    setBulkRunning(true);
    setBulkProgress({ total: targets.length, done: 0, succeeded: 0, failed: 0 });
    try {
      const { data } = await api.post("/admin/ronin-box/cues/spell-out-numbered");
      setBulkProgress({ total: data.total, done: data.total, succeeded: data.succeeded, failed: data.failed });
      toast.success(`Renamed ${data.renamed} cues, re-baked ${data.succeeded}/${data.renamed} audio${data.failed > 0 ? ` (${data.failed} failed)` : ""}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Spell-out failed");
    } finally {
      setBulkRunning(false);
    }
  };

  // Auto-fill the new movement_count field on every footwork + defense cue based on
  // movement-word inference from cue text ("Slip slip duck" = 3, "Pivot left" = 1, etc.).
  // By default only updates cues currently at 0; offers force-overwrite on a second pass.
  const autofillMovement = async () => {
    if (!window.confirm(
      `Auto-fill movement_count on every footwork + defense cue based on the cue text?\n\n` +
      `Counts movement words like "slip", "duck", "pivot", "roll", "weave", "step", "parry", "block", etc. ` +
      `"Slip slip duck" → 3 · "Pivot left" → 1 · "L-step pivot" → 2.\n\n` +
      `Only touches cues currently at 0 (your manual edits are preserved). You can force-overwrite after if needed.`
    )) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/admin/ronin-box/cues/autofill-movement", { force: false });
      if (data.updated === 0) {
        toast.info(`Scanned ${data.scanned} footwork/defense cues — all already have movement_count set. Nothing to do.`);
      } else {
        toast.success(`Auto-filled movement_count on ${data.updated} of ${data.scanned} cues (${data.skipped} skipped)`);
      }
      // Offer force-overwrite as a second pass if anything was skipped
      if (data.skipped > 0) {
        const force = window.confirm(
          `${data.skipped} cue${data.skipped === 1 ? "" : "s"} ${data.skipped === 1 ? "was" : "were"} skipped (already had movement_count set).\n\n` +
          `Want to FORCE-overwrite those too with the inferred values? This will replace any manual edits.`
        );
        if (force) {
          const r2 = await api.post("/admin/ronin-box/cues/autofill-movement", { force: true });
          toast.success(`Force-overwrote ${r2.data.updated} additional cue${r2.data.updated === 1 ? "" : "s"}`);
        }
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Autofill failed");
    } finally {
      setBulkRunning(false);
    }
  };

  // Bulk tag every numbered cue (digit form OR spelled-out form) for ALL modes + ALL
  // levels — fixes the case where the coach added numbered combos that only got
  // "shadow"+"bag" tags and silently disappeared from Defense workouts.
  const tagNumberedEverywhere = async () => {
    if (!window.confirm(
      `Tag every numbered cue (digits like "1-2-3" OR spelled-out like "One-Two-Three") for ALL modes (Shadow, Bag, Defense) and ALL levels (Beg, Int, Adv)?\n\n` +
      `This fixes the bug where numbered cues don't play in Defense Mode or Advanced level workouts because they were missing those tags.\n\n` +
      `Categories, weights, punches, audio are NOT touched. Mixed cues like "Jab one two" are skipped. Safe to re-run.`
    )) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/admin/ronin-box/cues/tag-numbered-everywhere");
      if (data.updated === 0) {
        toast.info(`Scanned ${data.scanned} cues — all numbered cues are already tagged for every mode + level. Nothing to do.`);
      } else {
        toast.success(`Tagged ${data.updated} numbered cue${data.updated === 1 ? "" : "s"} for all ${data.available_modes.length} modes + all ${data.levels.length} levels`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Tag-numbered failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const remove = async (cue) => {
    if (!window.confirm(`Delete cue "${cue.text}"?`)) return;
    try {
      await api.delete(`/admin/ronin-box/cues/${cue.id}`);
      setCues((cs) => cs.filter((c) => c.id !== cue.id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  // Cold-start recovery: if the cue library is empty, show a banner with a one-click seed action.
  const seedDefaults = async () => {
    try {
      const { data } = await api.post("/admin/ronin-box/seed-defaults");
      toast.success(`Seeded ${data.seeded} default cues. Click "Regenerate all" to bake audio.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Seed failed");
    }
  };

  return {
    cues, setCues, settings, setSettings, voices, loading, q, setQ, filterCat, setFilterCat,
    editing, setEditing, bulkRunning, bulkProgress, showSettings, setShowSettings,
    suggestOpen, setSuggestOpen, suggestions, setSuggestions, suggestLoading,
    selectedSuggestionIds, setSelectedSuggestionIds, applying,
    openSuggest, toggleSuggestion, applySuggestions, load, loadVoices, filtered, stats,
    regen, genMissing, regenAll, regenNumbered, spellOutNumbered, autofillMovement,
    tagNumberedEverywhere, remove, seedDefaults,
  };
}
